import { Badge } from "@shared/ui/badge";
import { Progress } from "@shared/ui/progress";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  CircleCheck,
  CirclePause,
  CircleX,
  PieChart,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useState } from "react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../../shared/ui/card";
import {
  useConsumerDashboard,
  useConsumerListViewData,
  usePlanDashboard,
} from "../../hooks";

interface ConsumerBreakdownTabProps {
  billCycleId: number;
  billDraftId: string }

export const ConsumerBreakdownTab:React.FC<ConsumerBreakdownTabProps> = ({
  billCycleId,
  billDraftId
}) => {
  console.log('billDraftId in ConsumerBreakdownTab ',billDraftId)
  const [consumerListCurrentPage, setConsumerListCurrentPage] = useState(1);

  const { data: planData } = usePlanDashboard(billCycleId);
  const { data: consumerListViewData } = useConsumerListViewData(
    billCycleId,
    billDraftId,
    consumerListCurrentPage
  );

  const { data: consumerData } = useConsumerDashboard(billCycleId);

  // Page change handler - following ConsumerList pattern
  const handlePageChange = (newPage: number) => {
    setConsumerListCurrentPage(newPage);
  };

  const consumerListColumns = [
    {
      key: "consumerNo",
      header: "Account No",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      render: (value: any, row: any) => {
        return `${row.firstName} ${row.lastName}`;
      },
    },
    {
      key: "utilityService",
      header: "Utility Service",
      render: (value: any) => {
        return Array.isArray(value) ? value.join(", ") : "N/A";
      },
    },
    {
      key: "category",
      header: "Category",
    },
    {
      key: "premise",
      header: "Premise",
    },
    {
      key: "statusDisplay",
      header: "Status",
      render: (value: any, row: any) => {
        return (
          <Badge className={getStatusColor(row.statusDisplay)}>
            {row.statusDisplay}
          </Badge>
        );
      },
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "temporary disconnect":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString();
  };

  // Get progress bar color based on category
  const getProgressColor = (category) => {
    switch (category?.toLowerCase()) {
      case "residential":
        return "bg-blue-600";
      case "commercial":
        return "bg-teal-500";
      case "industrial":
        return "bg-blue-700";
      default:
        return "bg-blue-500";
    }
  };

  return (
    <div className="space-y-4 py-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Consumers by Category
          </CardTitle>
          <CardDescription>
            Distribution of consumers across categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 pt-4">
            {consumerData?.categoryData?.map((item, index) => {
              const isPositive = Number(item?.percentage) > 0;

              return (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">
                      {item.category ?? "NA"}
                    </span>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {item.consumerCount}
                      {isPositive ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                      <span
                        className={
                          isPositive ? "text-green-500" : "text-red-500"
                        }
                      >
                        {item?.percentage}%
                      </span>
                    </div>
                  </div>
                  <Progress value={item.percentage * 10} className="h-2" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Cycle Comparison</CardTitle>
          <CardDescription>
            Consumer changes from previous cycle
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Previous Cycle
                </h4>
                <p className="card-value-text">
                  {planData?.previousConsumerCount ?? "NA"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  New Consumers
                </h4>
                <p className="card-value-text">
                  {planData?.newConsumerCount}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            Consumer Status
          </CardTitle>
          <CardDescription>Current status of all consumers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium">Active</span>
              </div>
              <p className="card-value-text">{consumerData?.active}</p>
              <span className="text-sm text-green-500">
                {consumerData?.activePercent}%
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CircleX className="h-5 w-5 text-red-500" />
                <span className="text-sm font-medium">Inactive</span>
              </div>
              <p className="card-value-text">{consumerData?.inactive}</p>
              <span className="text-sm text-red-500">
                {consumerData?.inactivePercent}%
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CirclePause className="h-5 w-5 text-yellow-500" />
                <span className="text-sm font-medium">Paused</span>
              </div>
              <p className="card-value-text">{consumerData?.paused}</p>
              <span className="text-sm text-yellow-500">
                {consumerData?.pausedPercent}%
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CirclePause className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-medium">
                  Temporary Disconnect
                </span>
              </div>
              <p className="card-value-text">{consumerData?.disconnected}</p>
              <span className="text-sm text-gray-500">
                {consumerData?.disconnectedPercent}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Consumer List</CardTitle>
          <CardDescription>
            Complete list of consumers in the current cycle
          </CardDescription>
        </CardHeader>
        <CardContent>
          <GenericTable
            data={consumerListViewData?.results ?? []}
            columns={consumerListColumns}
            rowKey="id"
            emptyMessage="No consumers found"
            pagination={{
              currentPage: consumerListCurrentPage,
              totalPages: Math.ceil(
                (consumerListViewData?.count || 0) / PAGE_SIZE
              ),
              totalItems: consumerListViewData?.count || 0,
              itemsPerPage: PAGE_SIZE,
              startItem: (consumerListCurrentPage - 1) * PAGE_SIZE + 1,
              endItem: Math.min(
                consumerListCurrentPage * PAGE_SIZE,
                consumerListViewData?.count || 0
              ),
            }}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};
